import { getHandle } from 'config/collections/utils/get-handle';
import { SegmentationIds } from 'constants/segmentation';
import { useRouteContext } from 'contexts/route';
import { SegmentationStore } from 'contexts/segmentation';
import { useRouter } from 'next/router';
import { useRegion } from 'hooks/use-region';
import { useMemo } from 'react';

interface Input {
  segmentation?: string | SegmentationStore;
}

interface OutPut {
  shouldOmitPersonalization: boolean;
}

const AGE_RELATED_SEGMENTATION_IDS_SET = new Set<SegmentationIds>([
  SegmentationIds.Welpe,
  SegmentationIds.DogSenior,
  SegmentationIds.Kitten,
  SegmentationIds.CatSenior,
  SegmentationIds.DogSenior,
  SegmentationIds.DogAdult,
  SegmentationIds.CatSenior,
  SegmentationIds.CatAdult,
]);

/**
 * Check if PDP and CDP are related to adult pet food based on current and previous url.
 * And check if we should omit personalization as combination might be confusing for user.
 * e.g Segmentation welpe and adult collection page.
 * @see : https://petsdeli.atlassian.net/browse/PT-5327
 */
const useIsAdultRelated = ({ segmentation }: Input): OutPut => {
  const { asPath } = useRouter();
  const region = useRegion();

  const { previousPath } = useRouteContext();

  const adultDogCdp = getHandle({ key: 'dogsAdult', region });
  const adultCatCdp = getHandle({ key: 'catsAllMeatTypes', region });

  // We assume that it's adult related when adult cdp and pdp pages afterwards
  const isAdultRelated = useMemo(() => {
    if (!segmentation) {
      return false;
    }

    const _segmentation =
      typeof segmentation === 'string' ? segmentation : segmentation.ageSegmentation;

    if (!_segmentation) {
      return false;
    }

    if (!AGE_RELATED_SEGMENTATION_IDS_SET.has(_segmentation as SegmentationIds)) {
      return false;
      // in age related cdp page
    } else if (asPath.includes(adultDogCdp) || asPath.includes(adultCatCdp)) {
      return true;
      // in pdp page after age related pdp
    } else if (
      previousPath &&
      (previousPath.includes(adultDogCdp) || previousPath.includes(adultCatCdp)) &&
      asPath.includes('product')
    ) {
      return true;
    }
    return false;
  }, [asPath, previousPath, segmentation, adultDogCdp, adultCatCdp]);

  const shouldOmitPersonalization = useMemo(() => {
    if (!segmentation) {
      return false;
    }

    const _segmentation =
      typeof segmentation === 'string' ? segmentation : segmentation.ageSegmentation;

    if (!_segmentation) {
      return false;
    }

    return !!(
      AGE_RELATED_SEGMENTATION_IDS_SET.has(_segmentation as SegmentationIds) && isAdultRelated
    );
  }, [isAdultRelated, segmentation]);

  return {
    shouldOmitPersonalization,
  };
};

export default useIsAdultRelated;
